import { globalEnv } from "~/global-env";
import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import { checkIsUsCountrySelected } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/AddressFields/StateFormField/utils";
import { ACCOUNT_TYPE_TO_VIEWABLE_ACCOUNT } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/EntityFields/constants";
import { getInvestingProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { findUsStateName } from "~/pages/Compliance/documents/CaseCreationPreview/stateMatcher";
import {
  isIndividualAccountType,
  isJointAccountType,
} from "~/pages/Compliance/utils/accountTypeUtils";
import { accountTypeFromMainAccountType } from "~/pages/Compliance/utils/mainAccountTypeUtils";
import { toDbId } from "~/utils/graphql-helpers";
import type { CompareCellBase, ComparisonResult } from "./types";
import type {
  AccountComparisonAddressFragment,
  AccountTypeEnum,
  ProfileComparisonQuery,
} from "~/graphql/supergraphTypes";
import type { PositionRowData } from "~/pages/Compliance/ExistingPositionsModal/types";
import type { ProfileNameFields } from "~/pages/Compliance/utils/types";

export type InvestmentAccountNode = NonNullable<
  NonNullable<ProfileComparisonQuery["rankedProfile"]>["investmentAccount"]
>;

/**
 * Minimal account-with-positions shape, satisfied by both the case's investment account (from
 * `ProfileComparison`) and each existing account (from `ProfileInvestmentAccountDetails`), so both
 * feed the same positions modal.
 */
export type AccountPositionsSource = {
  arena?: { id: string } | null;
  positions?: {
    edges: ReadonlyArray<{
      node: {
        id: string;
        complianceProgram?: ReadonlyArray<string | null> | null;
        account?: { name: string } | null;
        investmentEntity: { name: string };
        onboardingCase?: { investingProfile?: ProfileNameFields | null } | null;
      };
    }>;
  } | null;
};

/** Rows for an account's positions modal: shows the owner profile, or the entity when not onboarded. */
export type AccountPositionsModalData = {
  accountName: string;
  rows: PositionRowData[];
  viewAllHref: string;
};

export type AccountView = {
  name: string | null;
  taxId: string | null;
  accountType: string | null;
  /** Broad Individual/Joint/Entity category, distinct from the fine-grained `accountType`. */
  category: ComplianceObjectType | null;
  addressLines: string[];
  domicileCountry: string | null;
  clientName: string | null;
  positionCount: number;
  programs: string[];
  /** The account's positions, ready to render in the positions modal. */
  positionRows: PositionRowData[];
  /** Positions page filtered to this account's positions. */
  positionsHref: string;
};

type PositionPrograms = { complianceProgram?: readonly (string | null)[] | null };

export const isPresent = (value: string | null | undefined): value is string => Boolean(value);

const dedupe = (values: readonly (string | null | undefined)[]): string[] => [
  ...new Set(values.filter(isPresent)),
];

const normalizeForMatch = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, " ");

export const toCell = (
  value: string | null | undefined,
  subValue?: string | null,
): CompareCellBase => {
  if (!value) return {};
  return subValue ? { value, subValue } : { value };
};

export const toLinesCell = (lines: string[]): CompareCellBase =>
  lines.length ? { lines } : {};

export const toTagsCell = (tags: string[]): CompareCellBase => (tags.length ? { tags } : {});

export const joinAddressLines = (lines: readonly string[]): string | null =>
  lines.join(", ") || null;

/** Returns `null` only when both sides are blank. One side blank and the other present is a mismatch. */
export const comparisonStatus = (
  value: string | null | undefined,
  otherValue: string | null | undefined,
): ComparisonResult | null => {
  const hasValue = isPresent(value);
  const hasOtherValue = isPresent(otherValue);
  if (!hasValue && !hasOtherValue) return null;
  if (!hasValue || !hasOtherValue) return "mismatch";
  return normalizeForMatch(value) === normalizeForMatch(otherValue) ? "match" : "mismatch";
};

export const programsFromPositions = (positions: readonly PositionPrograms[]): string[] =>
  dedupe(positions.flatMap((position) => position.complianceProgram ?? []));

export const addressLines = (
  street: (string | null | undefined)[],
  city: string | null | undefined,
  regionAndPostal: (string | null | undefined)[],
  country: string | null | undefined,
): string[] => {
  const streetLine = street.filter(isPresent).join(" ");
  const cityLine = [city, regionAndPostal.filter(isPresent).join(" ")]
    .filter(isPresent)
    .join(", ");
  return [streetLine, cityLine, country].filter(isPresent);
};

export const viewableAccountType = (accountType: string | null | undefined): string | null =>
  accountType
    ? (ACCOUNT_TYPE_TO_VIEWABLE_ACCOUNT[accountType.toLowerCase()] ?? accountType)
    : null;

export const categoryFromAccountType = (
  accountType: AccountTypeEnum | null | undefined,
): ComplianceObjectType | null => {
  if (!accountType) return null;
  if (isJointAccountType(accountType)) return ComplianceObjectType.Joint;
  if (isIndividualAccountType(accountType)) return ComplianceObjectType.Individual;
  return null;
};

// Main returns a US state as a code (e.g. "CA"); expand it to a full name only for US addresses,
// since codes collide across countries (e.g. "WA" is also Western Australia).
const stateOrRegionName = (address: AccountComparisonAddressFragment): string | null => {
  const value = address.state?.objectName ?? address.region;
  if (!checkIsUsCountrySelected(address.country?.objectName)) return value;
  return findUsStateName(value) ?? value;
};

const composeLines = (
  address: AccountComparisonAddressFragment | null | undefined,
): string[] =>
  address
    ? addressLines(
        [address.street, address.street2, address.street3],
        address.city,
        [stateOrRegionName(address), address.postalCode],
        address.country?.objectName,
      )
    : [];

// Main omits the domicile address when it matches the mailing address, and a returned domicile
// can still be empty, so the lines and country each fall back to the mailing address on their own.
export const accountDomicile = (account: {
  domicileAddress?: AccountComparisonAddressFragment | null;
  mailingAddress?: AccountComparisonAddressFragment | null;
}): {
  lines: string[];
  country: string | null;
} => {
  const domicileLines = composeLines(account.domicileAddress);
  return {
    lines: domicileLines.length > 0 ? domicileLines : composeLines(account.mailingAddress),
    country:
      account.domicileAddress?.country?.objectName ??
      account.mailingAddress?.country?.objectName ??
      null,
  };
};

const decodeDbId = (id: string | null | undefined): number | undefined => {
  if (!id) return undefined;
  try {
    const dbId = toDbId(id);
    return Number.isNaN(dbId) ? undefined : dbId;
  } catch {
    return undefined;
  }
};

// Positions without a compliance case have no investing profile, so fall back to the investment
// entity name to keep every row identifiable.
export const accountPositionRows = (account: AccountPositionsSource): PositionRowData[] =>
  (account.positions?.edges ?? []).map((edge) => ({
    id: edge.node.id,
    profileName: edge.node.onboardingCase?.investingProfile
      ? getInvestingProfileName(edge.node.onboardingCase.investingProfile)
      : edge.node.investmentEntity.name,
    accountName: edge.node.account?.name ?? "Unassigned",
    programs: programsFromPositions([edge.node]),
  }));

export const accountPositionsHref = (account: AccountPositionsSource): string => {
  const arenaDbId = decodeDbId(account.arena?.id);
  const positionDbIds = (account.positions?.edges ?? [])
    .map((edge) => decodeDbId(edge.node.id))
    .filter((id): id is number => id !== undefined);

  const base = `/canopy${globalEnv.complianceRoute}/positions`;
  const params = new URLSearchParams();
  if (arenaDbId !== undefined) params.set("arenaIds", String(arenaDbId));
  if (positionDbIds.length > 0) params.set("positionIds", positionDbIds.join(","));

  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

/**
 * A position as served by `positionsSummary`, whose `name` is already formatted by main as
 * "{account name} in {investment entity name}", so it needs no client-side composition.
 */
export type PositionSummary = {
  id?: number | null;
  name?: string | null;
  complianceProgram?: readonly (string | null)[] | null;
};

export const summaryPositionRows = (summaries: readonly PositionSummary[]): PositionRowData[] =>
  summaries.map((summary) => ({
    id: String(summary.id ?? ""),
    name: summary.name ?? "",
    programs: programsFromPositions([summary]),
  }));

// `positionsSummary` ids are already plain db ids, and every row shares the arena the query was
// scoped to, so the deep link reads both directly rather than decoding relay ids.
export const summaryPositionsHref = (
  summaries: readonly PositionSummary[],
  arenaId: number | undefined,
): string => {
  const positionDbIds = summaries
    .map((summary) => summary.id)
    .filter((id): id is number => id != null);

  const base = `/canopy${globalEnv.complianceRoute}/positions`;
  const params = new URLSearchParams();
  if (arenaId !== undefined) params.set("arenaIds", String(arenaId));
  if (positionDbIds.length > 0) params.set("positionIds", positionDbIds.join(","));

  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

export const accountViewFromNode = (account: InvestmentAccountNode): AccountView => {
  // Normalize main's account type vocabulary (e.g. "JointOther") before deriving category/label.
  const accountType = accountTypeFromMainAccountType(account.accountType);
  const domicile = accountDomicile(account);
  return {
    name: account.name ?? account.legalEntityName ?? null,
    taxId: account.taxId ?? null,
    accountType: viewableAccountType(accountType ?? account.accountType),
    // A present-but-unmatched account type falls back to Entity rather than the "no type" null.
    category: account.accountType
      ? (categoryFromAccountType(accountType) ?? ComplianceObjectType.Institution)
      : null,
    addressLines: domicile.lines,
    domicileCountry: domicile.country,
    clientName: account.arena?.name ?? null,
    positionCount: account.positions?.count ?? 0,
    programs: programsFromPositions((account.positions?.edges ?? []).map((edge) => edge.node)),
    positionRows: accountPositionRows(account),
    positionsHref: accountPositionsHref(account),
  };
};
